import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { NavigationComponent } from './../../core/ui/navigation/navigation.component';
import { FooterComponent } from './../../core/ui/footer/footer.component';
import { HeaderComponent } from './../../core/ui/header/header.component';
import { PanelModule } from 'primeng/panel';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { DialogService } from 'primeng/dynamicdialog';
import { HeroComponent } from '../../core/ui/hero/hero.component';
import { Constants } from '../../common/helper/Constants';
import { ImpersonationStatusComponent } from '../../core/ui/impersonation-status/impersonation-status.component';
import { SpeedDial } from 'primeng/speeddial';
import { MenuItem } from 'primeng/api';
import { TranslatePipe } from '@ngx-translate/core';
import { UserService } from '../../common/service/user.service';
import { Role } from '../../core/auth/role.enum';
import { PlatformService } from '../../common/service/platform.service';
import { CustomerService } from '../../common/service/customer.service';
import { NotificationService } from '../../common/service/notification.service';
import { Customer } from '../../common/model/customer/customer';
import { CustomerMenuItem } from '../../common/model/customer/customer-menu-item';
import { BreadcrumbComponent } from '../../core/ui/header/breadcrumb/breadcrumb.component';
@Component({
  selector: 'gate-main-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    NavigationComponent,
    PanelModule,
    HeroComponent,
    FooterComponent,
    HeaderComponent,
    CommonModule,
    ButtonModule,
    ToastModule,
    RouterLink,
    RouterLinkActive,
    ImpersonationStatusComponent,
    SpeedDial,
    TranslatePipe,
    BreadcrumbComponent
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  providers: [DialogService],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent implements OnInit {
  public routes = Constants.routes;
  private router = inject(Router);
  private userService = inject(UserService);
  private customerService = inject(CustomerService);
  private notificationService = inject(NotificationService);
  public isAccountManager = computed(() => this.userService.currentUser().roles.includes(Role.AccountManager));
  public customer: Customer | undefined;
  public isCustomerLoading = false;
  public currentCustomerName = '';
  public currentCustomerId = '';
  public customersMenuItem: CustomerMenuItem[] = [];
  public preferredCustomerLoadingId: string | null = null;
  private customerMenuEntries: Array<{ customerId: string; label: string }> = [];
  private customerBatchMap = new Map<string, Customer>();
  private currentCustomerRequestId = 0;
  public isImpersonatingAsAcpUser = computed(() => this.userService.isImpersonating() && this.impersonationContext()?.initiatorType == 'ACP_User');
  private impersonationContext = computed(() => this.userService.getImpersonationContext());

  public speedDialMenu:MenuItem[] = [
    {
      label: 'menu.help',
      icon: 'assets/icons/support_r_1000px_rgb.png',
      command: () => {
        window.open('https://www.acp-gruppe.com/gate/faq/', '_blank');
                },
    },
    {
      label: 'menu.feedback',
      icon: 'assets/icons/communication_r_1000px_rgb.png',
      command: () => {
            this.router.navigate([this.routes.feedback()]);
      }
    }];

  private platformService = inject(PlatformService);
  public sidebarOpen = false;
  public desktopSidebarOpen = signal(true);
  
  ngOnInit(): void {
    this.loadCustomerMenuItems();

    const selectedCustomerId = localStorage.getItem('customerId') || this.userService.currentUser().customerId;
    if (selectedCustomerId) {
      this.syncCurrentCustomer(selectedCustomerId);
      if (this.userService.currentUser().assignedCustomers.length > 0) {
        this.ensurePreferredCustomerExists(selectedCustomerId);
      }
    }

    this.userService.impersonationStatus.subscribe((customerId) => {
      this.syncCurrentCustomer(customerId || this.userService.currentUser().customerId);
    });
    this.userService.preferredCustomerUpdated.subscribe(() => {
      this.updateCustomerMenuItems();
    });
    this.customerService.customerLogoUpdated.subscribe((customerId) => {
      if (!customerId) {
        return;
      }

      const cachedCustomer = this.customerService.getCachedCustomer(customerId);
      if (cachedCustomer) {
        this.customerBatchMap.set(customerId, cachedCustomer);

        if (customerId === this.currentCustomerId) {
          this.customer = cachedCustomer;
          this.currentCustomerName = cachedCustomer.fullName ?? this.currentCustomerName;
          this.isCustomerLoading = false;
          this.updateCustomerMenuItems();
        }
      } else if (customerId === this.currentCustomerId) {
        this.syncCurrentCustomer(customerId, true);
      }
    });

    this.desktopSidebarOpen.set(this.platformService.getIsDesktopSidebarOpen());
    this.platformService.desktopSidebarToggleEvent.subscribe((value) => {
      this.desktopSidebarOpen.set(value);
    });

    if (this.isAccountManager()) {
      this.speedDialMenu.splice(1, 0, {
        label: 'menu.helpinternal',
        icon: 'assets/icons/support_r_1000px_rgb.png',
        command: () => {
          window.open('https://www.acp-gruppe.com/gate/faq/intern', '_blank');
        },
      });
    }
  }
  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }
  onClickMenu() {
    this.sidebarOpen = false;
  }

  public switchCustomer(customerId: string) {
    if (!this.userService.currentUser().roles.includes(Role.Customer)) {
      return;
    }

    if (!this.customer || this.customer.id !== customerId) {
      this.currentCustomerId = customerId;
      this.currentCustomerName = this.customerMenuEntries.find((item) => item.customerId === customerId)?.label ?? this.currentCustomerName;
      this.customer = undefined;
      this.isCustomerLoading = true;
      this.updateCustomerMenuItems();
      this.userService.switchCustomer(customerId);
    }
  }

  public setPreferredCustomer(customerId: string) {
    if (this.preferredCustomerLoadingId || this.userService.getPreferredCustomerNumber() === customerId) {
      return;
    }

    this.preferredCustomerLoadingId = customerId;
    this.updateCustomerMenuItems();

    this.userService.updatePreferredCustomer(customerId)
      .then(() => {
        this.notificationService.showNotification(
          'success',
          'notification.customerPreference.success.title',
          'notification.customerPreference.success.message'
        );
        this.updateCustomerMenuItems();
      })
      .catch((error) => {
        console.error('Error setting preferred customer:', error);
        this.notificationService.showNotification(
          'error',
          'notification.customerPreference.error.title',
          'notification.customerPreference.error.message'
        );
      })
      .finally(() => {
        this.preferredCustomerLoadingId = null;
        this.updateCustomerMenuItems();
      });
  }

  private syncCurrentCustomer(customerId: string | null | undefined, forceRefresh = false) {
    const requestId = ++this.currentCustomerRequestId;

    if (!customerId) {
      if (requestId === this.currentCustomerRequestId) {
        this.customer = undefined;
        this.currentCustomerName = '';
        this.currentCustomerId = '';
        this.isCustomerLoading = false;
        this.updateCustomerMenuItems();
      }
      return;
    }

    const fallbackName = this.customerMenuEntries.find((item) => item.customerId === customerId)?.label ?? '';
    this.currentCustomerId = customerId;
    if (fallbackName) {
      this.currentCustomerName = fallbackName;
    }

    const batchCustomer = forceRefresh
      ? undefined
      : this.customerService.getCachedCustomer(customerId) ?? this.customerBatchMap.get(customerId);
    if (batchCustomer) {
      this.customerBatchMap.set(customerId, batchCustomer);
      this.customer = batchCustomer;
      this.currentCustomerName = batchCustomer.fullName ?? fallbackName;
      this.isCustomerLoading = false;
      this.updateCustomerMenuItems();
      return;
    }

    this.isCustomerLoading = true;
    this.customerService.getCustomersBatch([customerId])
      .then((customers) => {
        if (requestId !== this.currentCustomerRequestId) {
          return;
        }
        const customer = customers.find((item) => item.id === customerId) ?? customers[0];
        if (!customer) {
          throw new Error(`Customer ${customerId} not found in batch response.`);
        }
        this.customerBatchMap.set(customer.id, customer);
        this.customer = customer;
        this.currentCustomerName = customer.fullName ?? '';
      })
      .catch(() => {
        if (requestId !== this.currentCustomerRequestId) {
          return;
        }
        this.customer = undefined;
        this.currentCustomerName = this.customerMenuEntries.find((item) => item.customerId === customerId)?.label ?? '';
      })
      .finally(() => {
        if (requestId !== this.currentCustomerRequestId) {
          return;
        }
        this.isCustomerLoading = false;
        this.updateCustomerMenuItems();
      });
  }

  private loadCustomerMenuItems() {
    const assigned = this.userService.currentUser().assignedCustomers;
    const ids = assigned.map((customer) => customer.groupId);

    if (ids.length === 0) {
      this.customerMenuEntries = [];
      this.updateCustomerMenuItems();
      return;
    }

    this.customerService.getCustomersBatch(ids)
      .then((customers) => {
        const customerMap = new Map(customers.map((customer) => [customer.id, customer]));
        this.customerBatchMap = customerMap;

        this.customerMenuEntries = assigned.map((assignedCustomer) => {
          const customer = customerMap.get(assignedCustomer.groupId);

          return {
            customerId: assignedCustomer.groupId,
            label: customer?.fullName || assignedCustomer.groupName,
          };
        });
      })
      .catch((error) => {
        console.error('Error loading customer menu items in batch:', error);
        this.customerBatchMap.clear();
        this.customerMenuEntries = assigned.map((customer) => ({
          customerId: customer.groupId,
          label: `${customer.groupName}`,
        }));
      })
      .finally(() => {
        const batchCustomer = this.currentCustomerId ? this.customerBatchMap.get(this.currentCustomerId) : undefined;
        if (batchCustomer && (!this.customer || this.customer.id === this.currentCustomerId)) {
          this.customer = batchCustomer;
          this.currentCustomerName = batchCustomer.fullName ?? this.currentCustomerName;
        }

        if (!this.customer?.fullName && this.currentCustomerId) {
          this.currentCustomerName = this.customerMenuEntries.find((item) => item.customerId === this.currentCustomerId)?.label ?? this.currentCustomerName;
        }
        this.updateCustomerMenuItems();
      });
  }

  private updateCustomerMenuItems() {
    const currentCustomerId = this.currentCustomerId || this.userService.currentUser().customerId;
    const preferredCustomerId = this.userService.getPreferredCustomerNumber();

    this.customersMenuItem = this.customerMenuEntries
      .map((item) => ({
        ...item,
        isCurrent: item.customerId === currentCustomerId,
        isPreferred: item.customerId === preferredCustomerId,
        isLoading: item.customerId === this.preferredCustomerLoadingId,
      }))
      .sort((a, b) => {
        if (a.isPreferred !== b.isPreferred) {
          return a.isPreferred ? -1 : 1;
        }

        return a.label.localeCompare(b.label);
      });
  }

  private ensurePreferredCustomerExists(selectedCustomerId: string | null | undefined) {
    if (!selectedCustomerId || this.userService.getPreferredCustomerNumber() || this.preferredCustomerLoadingId) {
      return;
    }

    this.preferredCustomerLoadingId = selectedCustomerId;
    this.updateCustomerMenuItems();

    this.userService.updatePreferredCustomer(selectedCustomerId)
      .then(() => {
        this.updateCustomerMenuItems();
      })
      .catch((error) => {
        console.error('Error setting initial preferred customer:', error);
      })
      .finally(() => {
        this.preferredCustomerLoadingId = null;
        this.updateCustomerMenuItems();
      });
  }
}
